import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import moment from 'moment';

import { isObjNilOrEmpty } from 'utils/validator';

import { IRule, IRuleOutcome, RuleType, TriggerType } from '../types';
import { defaultValues, RuleTriggerDayTimeItem } from './fields/RuleTriggerDayTime';

export const getTriggerType = (event_name: string = ''): TriggerType => {
  if (!event_name) return 'reward';

  const loyaltyTriggers = ['achieved_next_tier', 'near_tier', 'points_issued', 'ageing_points'];
  const rewardTriggers = [
    'voucher_redeemed',
    'voucher_issued',
    'voucher_issued_reward',
    'voucher_issued_campaign',
    'voucher_issued_merchant',
    'voucher_expiry',
    'voucher_expiry_particular_reward'
  ];
  const milestoneTriggers = [
    'stamp_redeemed',
    'achieve_stamps_slot',
    'inactive_stamp_card',
    'campaign_completed',
    'campaign_not_completed',
    'game_redeemed',
    'game_try_issued',
    'campaign_enrolled',
    'campaign_outcome_issued',
    'fuel_purchase_exceeds_10k',
    'progress_rule_trigger',
    'quest_task_completed',
    'achieved_progress_milestone'
  ];
  const customerTriggers = [
    'customer_signed_up',
    'customer_signed_in',
    'customer_profile_updated',
    'user_reactivated'
  ];
  const transactionTriggers = ['transactions_created'];
  const locationTriggers = ['checkin'];
  const cumulativeSpend = ['cumulative_spend'];

  if (loyaltyTriggers.includes(event_name)) {
    return 'loyalty';
  } else if (rewardTriggers.includes(event_name)) {
    return 'reward';
  } else if (milestoneTriggers.includes(event_name)) {
    return 'milestone';
  } else if (customerTriggers.includes(event_name)) {
    return 'customer';
  } else if (transactionTriggers.includes(event_name)) {
    return 'transaction';
  } else if (locationTriggers.includes(event_name)) {
    return 'location';
  } else if (cumulativeSpend.includes(event_name)) {
    return 'Cumulative Spend';
  } else {
    return 'others';
  }
};

export const normalizeVoucherExpiryEvent = (formData: any) => {
  if (!formData || !formData.event_params) return formData;

  const hasRewardCampaignId = !isNil(formData.event_params.reward_campaign_id);

  if (formData.event_name === 'voucher_expiry' && hasRewardCampaignId) {
    return {
      ...formData,
      event_name: 'voucher_expiry_particular_reward'
    };
  }

  if (formData.event_name === 'voucher_expiry_particular_reward' && !hasRewardCampaignId) {
    return {
      ...formData,
      event_name: 'voucher_expiry'
    };
  }

  return formData;
};

const generateFormInitValues = (fData: any, fn: Function, ruleType: RuleType, availableLocales) => {
  const nameObject = availableLocales.reduce((acc, locale) => {
    acc[`name_${locale}`] = null;
    return acc;
  }, {});
  return ruleType === 'trigger'
    ? {
        name: null,
        ...nameObject,
        labels: [],
        rule_type: 'trigger',
        event_name: 'app_trigger', // otherwise placeholder won't show up
        begins_at: fn(),
        ends_at: null,
        object_owner_id: null,
        event_params: {
          reward_id: undefined,
          campaign_id: undefined,
          automation_trigger_id: undefined,
          tier_id: undefined,
          number_of_stamps: undefined,
          stamp_position: undefined,
          outcome_id: undefined,
          modularizable_id: undefined,
          modularizable_type: undefined,
          points_count: undefined,
          outcome_value: undefined
        },
        rule_trigger_days: defaultValues,
        trigger_limit: undefined,
        account_limit: undefined,

        outcome_type: 'Campaign',
        outcome_id: undefined,
        outcome_value: undefined, // might have problem in reinit edit form

        outcome_audience_to_all: undefined,
        include_outcome_audience_ids: [],
        exclude_outcome_audience_ids: [],
        temp: {
          // just for frontend usage only
          // to dynamically render fields
          event_name: undefined,
          trigger_type: null,
          outcome_campaign_type: 'stamp',
          rule_trigger_checked: false,

          // Step 2
          selection_for_audience_list: []
        },

        audience_ids: [],
        include_audience_ids: [],
        exclude_audience_ids: [],
        filters: {
          // race: [{ type: 'race', criteria: [{ policy: 'include', values: [] }] }]
        },
        send_notification: false,
        partner_id: fData?.partner?.id || undefined,
        outcome_expires_at: null,
        outcome_options: {
          selected_media_type_id: null,
          custom_fields: {}
        }
      }
    : {
        name: null,
        rule_type: ruleType,
        conditions: [],
        event_params: { automation_trigger_id: null },
        temp: { trigger_type: null },
        event_name: 'app_trigger',
        earn_type: null,
        outcome_id: null,
        outcome_type: null,
        outcome_value: null,
        outcome_base: null,
        max_outcome_value: null,
        object_owner_id: null
      };
};

export const formatFormData = (
  formData,
  isEdit = false,
  ruleType: RuleType = 'trigger',
  availableLocales = []
) => {
  const formInitialValues = generateFormInitValues(formData, moment, ruleType, availableLocales);

  if (isObjNilOrEmpty(formData)) return formInitialValues;

  formData = normalizeVoucherExpiryEvent(formData);

  // Re-initialize the form
  if (typeof formData.event_name === 'string' && formData.event_name.startsWith('voucher_issued')) {
    let voucherType;
    if (formData.event_params.hasOwnProperty('campaign_id')) {
      voucherType = 'voucher_issued_campaign';
    } else if (formData.event_params.hasOwnProperty('merchant_account_id')) {
      voucherType = 'voucher_issued_merchant';
    } else {
      voucherType = 'voucher_issued_reward';
    }

    formData.temp = { event_name: voucherType };
  }

  for (let key in formData) {
    if (isNil(formData[key])) {
      delete formData[key]; // to prevent validation errors on Formik
    } else if (key === 'labels') {
      formData[key] = formData[key].map(l => l.id);
    }
  }
  const isRuleTriggerDaysChecked = (formData.rule_trigger_days || []).length > 0;
  return {
    ...formInitialValues,
    ...formData,
    name: isEdit ? formData.name : `${formData.name} Duplicate`,
    name_en: isEdit ? formData.name : `${formData.name} Duplicate`,
    rule_trigger_days: !isRuleTriggerDaysChecked ? defaultValues : formData.rule_trigger_days,
    temp: {
      event_name: formData.event_name,
      ...formData.temp,
      trigger_type: getTriggerType(formData.event_name) || null,
      selection_for_audience_list: formData.selection_for_audience_list,
      rule_trigger_checked: isRuleTriggerDaysChecked
    }
  };
};

export const doesRuleTriggerDayHasValue = (item: RuleTriggerDayTimeItem) => {
  const key: keyof RuleTriggerDayTimeItem = 'selected_values';
  if (!_has(item, key)) return false;

  if (Array.isArray(item[key])) {
    // Check if the array has any elements
    return item[key].length > 0;
  }

  if (typeof item[key] === 'string') {
    // Check if the string is non-empty
    return !_isEmpty(item[key]);
  }

  return false;
};

export function cleanOutcomeOptions(outcomeOptions) {
  const { outcome_properties, ...rest } = outcomeOptions;

  const gl_account_id = outcome_properties?.gl_account_id;
  const gl_account_type = outcome_properties?.gl_account_type;
  const gl_account_name = outcome_properties?.gl_account_name;

  const hasGlAccountId = gl_account_id !== undefined && gl_account_id !== null;
  const hasGlAccountType = Boolean(gl_account_type);
  const hasGlAccountName = Boolean(gl_account_name);

  if (!hasGlAccountId && !hasGlAccountType && !hasGlAccountName) {
    return rest;
  }

  const newOutcomeProperties: Record<string, any> = {};

  if (hasGlAccountId) {
    newOutcomeProperties.gl_account_id = gl_account_id;
  }
  if (hasGlAccountType) {
    newOutcomeProperties.gl_account_type = gl_account_type;
  }
  if (hasGlAccountName) {
    newOutcomeProperties.gl_account_name = gl_account_name;
  }

  return {
    ...rest,
    outcome_properties: newOutcomeProperties
  };
}

export const scopedName = (namePrefix: string, name: string): string =>
  namePrefix ? `${namePrefix}.${name}` : name;

/** Deliberately excludes `id`: on a legacy rule that key is the rule id, not an outcome row id. */
export const OUTCOME_FIELD_KEYS = [
  'outcome_type',
  'outcome_id',
  'outcome_value',
  'outcome_base',
  'max_outcome_value',
  'outcome_expires_at',
  'send_notification',
  'outcome_options'
];

const OUTCOME_API_KEYS = ['id', ...OUTCOME_FIELD_KEYS];

const pickOutcomeFields = (
  source: Record<string, any>,
  keys: string[],
  isSkipped: (value: any) => boolean = isNil
): IRuleOutcome => {
  const outcome: Record<string, any> = {};

  keys.forEach(key => {
    if (!isSkipped(source[key])) outcome[key] = source[key];
  });

  return outcome as IRuleOutcome;
};

// A saved notification-only row comes back as `outcome_type: null, send_notification: true`, and the
// type selector has no option for null, so the row has to read back as 'Push Event Only' (PLA-9364).
const withNotifyOnlyType = (outcome: IRuleOutcome): IRuleOutcome =>
  isNil(outcome.outcome_type) && outcome.send_notification
    ? { ...outcome, outcome_type: 'Push Event Only' }
    : outcome;

export const toFormOutcomes = (rule: Partial<IRule> = {}): IRuleOutcome[] => {
  if (Array.isArray(rule?.outcomes) && rule.outcomes.length > 0) {
    return [...rule.outcomes]
      .map((outcome, index) =>
        withNotifyOnlyType({ ...outcome, position: outcome.position ?? index + 1 })
      )
      .sort((a, b) => (a.position as number) - (b.position as number));
  }

  const legacyOutcome = pickOutcomeFields(rule ?? {}, OUTCOME_FIELD_KEYS);

  if (rule?.send_notification) legacyOutcome.outcome_type = 'Push Event Only';

  return [{ ...legacyOutcome, position: 1 }];
};

export const toApiOutcomes = (values: Record<string, any> = {}): IRuleOutcome[] => {
  const hasRows = Array.isArray(values?.outcomes) && values.outcomes.length > 0;
  const rows = hasRows ? values.outcomes : toFormOutcomes(values);

  return rows.map((row: Record<string, any>, index: number) => {
    // Only `undefined` is dropped: a key the user cleared arrives as an explicit `null` and has to
    // reach the API as one, or clearing a saved value is a silent no-op (PLA-9364).
    const outcome = pickOutcomeFields(row, OUTCOME_API_KEYS, value => value === undefined);

    if (outcome.outcome_type === 'Push Event Only') outcome.outcome_type = null;

    return { ...outcome, position: index + 1 };
  });
};
